import * as yup from "yup";
import { FormikHelpers, useFormik } from "formik";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useLayoutEffect, useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Autocomplete from "@mui/material/Autocomplete";
import FormHelperText from "@mui/material/FormHelperText";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useAuth } from "../../../../features/auth/hooks/use-auth";
import { useCore } from "../../../../shared/hooks/use-core";
import { LoadingPage } from "../../../../shared/components/loading-page";
import NotAuthorized from "../NotAuthorized";
import { doorRuleActiveLabel } from "../DoorRuleToggle";
import {
	DoorRuleRequestError,
	createDoorRule,
	getDoorRule,
	getDoorRuleOptions,
	updateDoorRule,
} from "../../../../features/precificador/services/door-count-rule";
import {
	DoorRuleOption,
	DoorRuleOptions,
	DoorRulePayload,
	DoorRuleProps,
	KeywordSource,
} from "../../../../features/precificador/types/door-rule";

const LIST_PATH = "/precificador-fipe?tab=regras-portas";
const LOAD_ERROR_TOAST_ID = "door-rule-load-error";
const REQUIRED = "Campo obrigatório";
const MAX_NAME_LENGTH = 120;
const MIN_DOORS = 1;
const MAX_DOORS = 6;
const MIN_PRIORITY = 0;
const MAX_PRIORITY = 1000;
const DEFAULT_PRIORITY = 100;
const MAX_KEYWORDS = 5;
const MAX_SEATS = 10;
const INVALID_DOORS = `Informe um número inteiro de ${MIN_DOORS} a ${MAX_DOORS}`;
const INVALID_PRIORITY = `Informe um número inteiro de ${MIN_PRIORITY} a ${MAX_PRIORITY}`;
const MISSING_FILTER =
	"Informe pelo menos um filtro: espécie, marca, palavra-chave ou lugares.";
const SEAT_OPTIONS = Array.from({ length: 99 }, (_, index) => index + 1);

interface Values {
	name: string;
	door_count: string;
	priority: string;
	vehicle_types: string[];
	species: string;
	brand: string;
	keywords: string[];
	keyword_source: KeywordSource | "";
	seats: number[];
	active: boolean;
}

type FieldErrors = Partial<Record<keyof Values | "filters", string>>;

const FIELD_ERROR_MESSAGES: Partial<Record<keyof Values, string>> = {
	name: `Informe um nome de até ${MAX_NAME_LENGTH} caracteres`,
	door_count: INVALID_DOORS,
	priority: INVALID_PRIORITY,
	vehicle_types: "Selecione pelo menos um tipo de veículo",
	species: "Espécie inválida",
	brand: "Marca inválida",
	keywords: "Palavras-chave inválidas",
	keyword_source: "Origem da palavra-chave inválida",
	seats: "Lugares inválidos",
};

const emptyValues: Values = {
	name: "",
	door_count: "",
	priority: String(DEFAULT_PRIORITY),
	vehicle_types: [],
	species: "",
	brand: "",
	keywords: [],
	keyword_source: "",
	seats: [],
	active: true,
};

const ruleToValues = (rule: DoorRuleProps): Values => ({
	name: rule.name,
	door_count: String(rule.door_count),
	priority: String(rule.priority),
	vehicle_types: rule.vehicle_types,
	species: rule.species ?? "",
	brand: rule.brand ?? "",
	keywords: rule.keywords,
	keyword_source: rule.keyword_source ?? "",
	seats: rule.seats,
	active: rule.active,
});

const integerInRange = (min: number, max: number) => (value?: string) => {
	const number = Number(value);
	return Number.isInteger(number) && number >= min && number <= max;
};

const validationSchema = yup
	.object({
		name: yup
			.string()
			.trim()
			.required(REQUIRED)
			.max(MAX_NAME_LENGTH, `Use no máximo ${MAX_NAME_LENGTH} caracteres`),
		door_count: yup
			.string()
			.trim()
			.required(REQUIRED)
			.test("doors-range", INVALID_DOORS, integerInRange(MIN_DOORS, MAX_DOORS)),
		priority: yup
			.string()
			.trim()
			.required(REQUIRED)
			.test(
				"priority-range",
				INVALID_PRIORITY,
				integerInRange(MIN_PRIORITY, MAX_PRIORITY),
			),
		vehicle_types: yup.array().min(1, FIELD_ERROR_MESSAGES.vehicle_types),
		species: yup.string(),
		brand: yup.string(),
		keywords: yup
			.array()
			.max(MAX_KEYWORDS, `Informe no máximo ${MAX_KEYWORDS} palavras-chave`),
		keyword_source: yup
			.string()
			.test(
				"keyword-source-required",
				"Escolha onde procurar as palavras-chave",
				function (value) {
					return this.parent.keywords.length === 0 || !!value;
				},
			),
		seats: yup
			.array()
			.max(MAX_SEATS, `Escolha no máximo ${MAX_SEATS} quantidades de lugares`),
	})
	.test("at-least-one-filter", MISSING_FILTER, function (values) {
		const hasFilter =
			!!values.species ||
			!!values.brand ||
			values.keywords!.length > 0 ||
			values.seats!.length > 0;
		return (
			hasFilter ||
			this.createError({ path: "filters", message: MISSING_FILTER })
		);
	});

const toPayload = (values: Values): Required<DoorRulePayload> => ({
	name: values.name.trim(),
	door_count: Number(values.door_count),
	priority: Number(values.priority),
	vehicle_types: values.vehicle_types,
	species: values.species || null,
	brand: values.brand || null,
	keywords: values.keywords,
	keyword_source:
		values.keywords.length > 0 && values.keyword_source
			? values.keyword_source
			: null,
	seats: values.seats,
	active: values.active,
});

const isEmptyValue = (value: unknown) =>
	value === null || (Array.isArray(value) && value.length === 0);

const createPayload = (values: Values): DoorRulePayload =>
	Object.fromEntries(
		Object.entries(toPayload(values)).filter(
			([, value]) => !isEmptyValue(value),
		),
	);

const changedPayload = (initial: Values, values: Values): DoorRulePayload => {
	const before = toPayload(initial);
	return Object.fromEntries(
		Object.entries(toPayload(values)).filter(
			([key, value]) =>
				JSON.stringify(value) !==
				JSON.stringify(before[key as keyof DoorRulePayload]),
		),
	);
};

const findOption = (list: DoorRuleOption[], value: string) =>
	list.find((option) => option.value === value) ?? null;

type DoorRuleFormProps = {
	id?: string;
	options: DoorRuleOptions;
	initialValues: Values;
};

function DoorRuleForm({
	id,
	options,
	initialValues,
}: DoorRuleFormProps): JSX.Element {
	const navigate = useNavigate();

	const handleSave = async (
		values: Values,
		{ setFieldError }: FormikHelpers<Values>,
	) => {
		try {
			if (id) {
				const payload = changedPayload(initialValues, values);
				if (Object.keys(payload).length === 0) {
					toast.info("Nenhuma alteração para salvar.", {
						position: toast.POSITION.TOP_RIGHT,
					});
					return;
				}
				await updateDoorRule(id, payload);
				toast.success("Regra atualizada com sucesso!", {
					position: toast.POSITION.TOP_RIGHT,
				});
			} else {
				await createDoorRule(createPayload(values));
				toast.success("Regra cadastrada com sucesso!", {
					position: toast.POSITION.TOP_RIGHT,
				});
			}
			navigate(LIST_PATH);
		} catch (error) {
			const field =
				error instanceof DoorRuleRequestError
					? (error.field as keyof Values | undefined)
					: undefined;
			if (field && FIELD_ERROR_MESSAGES[field]) {
				setFieldError(field, FIELD_ERROR_MESSAGES[field]);
				return;
			}
			const message =
				error instanceof Error
					? error.message
					: "Não foi possível salvar a regra.";
			toast.error(message, { position: toast.POSITION.TOP_RIGHT });
		}
	};

	const {
		values,
		errors,
		touched,
		submitCount,
		isSubmitting,
		handleChange,
		handleSubmit,
		setFieldValue,
		setFieldTouched,
	} = useFormik({
		initialValues,
		validationSchema,
		onSubmit: handleSave,
	});

	const fieldErrors = errors as FieldErrors;
	const fieldError = (field: keyof Values) =>
		(touched[field] || submitCount > 0) && fieldErrors[field]
			? fieldErrors[field]
			: undefined;
	const filtersError = submitCount > 0 ? fieldErrors.filters : undefined;

	return (
		<Box component="form" noValidate onSubmit={handleSubmit} sx={{ p: 2 }}>
			<Typography variant="h6" sx={{ mb: 2 }}>
				Dados da regra
			</Typography>

			<Grid container spacing={2}>
				<Grid item xs={12} md={6}>
					<TextField
						fullWidth
						required
						id="name"
						name="name"
						label="Nome"
						placeholder="ex.: Corolla sedã"
						value={values.name}
						onChange={handleChange}
						onBlur={() => setFieldTouched("name", true)}
						error={!!fieldError("name")}
						helperText={fieldError("name") ?? "Identifica a regra na listagem."}
					/>
				</Grid>

				<Grid item xs={12} md={3}>
					<TextField
						fullWidth
						required
						id="door_count"
						name="door_count"
						type="number"
						label="Quantidade de portas"
						inputProps={{ min: MIN_DOORS, max: MAX_DOORS, step: 1 }}
						value={values.door_count}
						onChange={handleChange}
						onBlur={() => setFieldTouched("door_count", true)}
						error={!!fieldError("door_count")}
						helperText={
							fieldError("door_count") ??
							"Atribuída aos veículos que se encaixam na regra."
						}
					/>
				</Grid>

				<Grid item xs={12} md={3}>
					<TextField
						fullWidth
						required
						id="priority"
						name="priority"
						type="number"
						label="Prioridade"
						inputProps={{ min: MIN_PRIORITY, max: MAX_PRIORITY, step: 1 }}
						value={values.priority}
						onChange={handleChange}
						onBlur={() => setFieldTouched("priority", true)}
						error={!!fieldError("priority")}
						helperText={
							fieldError("priority") ??
							"Maior vence quando mais de uma regra serve."
						}
					/>
				</Grid>

				<Grid item xs={12}>
					<Autocomplete
						multiple
						disableCloseOnSelect
						options={options.vehicle_types}
						getOptionLabel={(option) => option.label}
						isOptionEqualToValue={(option, value) =>
							option.value === value.value
						}
						value={options.vehicle_types.filter((option) =>
							values.vehicle_types.includes(option.value),
						)}
						onChange={(_event, selected) =>
							setFieldValue(
								"vehicle_types",
								selected.map((option) => option.value),
							)
						}
						onBlur={() => setFieldTouched("vehicle_types", true)}
						renderInput={(params) => (
							<TextField
								{...params}
								required
								label="Tipos de veículo"
								error={!!fieldError("vehicle_types")}
								helperText={
									fieldError("vehicle_types") ??
									"A regra vale só para os tipos escolhidos."
								}
							/>
						)}
					/>
				</Grid>

				<Grid item xs={12}>
					<Typography variant="subtitle2" sx={{ mt: 1 }}>
						Filtros
					</Typography>
					<FormHelperText error={!!filtersError}>
						{filtersError ??
							"Informe pelo menos um. Filtro em branco vale para qualquer valor."}
					</FormHelperText>
				</Grid>

				<Grid item xs={12} md={6}>
					<Autocomplete
						options={options.species}
						getOptionLabel={(option) => option.label}
						isOptionEqualToValue={(option, value) =>
							option.value === value.value
						}
						value={findOption(options.species, values.species)}
						onChange={(_event, option) =>
							setFieldValue("species", option?.value ?? "")
						}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Espécie"
								helperText="Em branco, vale para todas as espécies."
							/>
						)}
					/>
				</Grid>

				<Grid item xs={12} md={6}>
					<Autocomplete
						options={options.brands}
						getOptionLabel={(option) => option.label}
						isOptionEqualToValue={(option, value) =>
							option.value === value.value
						}
						value={findOption(options.brands, values.brand)}
						onChange={(_event, option) =>
							setFieldValue("brand", option?.value ?? "")
						}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Marca"
								placeholder="Digite para buscar"
								helperText="Em branco, vale para todas as marcas."
							/>
						)}
					/>
				</Grid>

				<Grid item xs={12} md={6}>
					<Autocomplete
						multiple
						freeSolo
						options={[] as string[]}
						value={values.keywords}
						onChange={(_event, keywords) =>
							setFieldValue(
								"keywords",
								Array.from(
									new Set(
										keywords.map((keyword) => keyword.trim()).filter(Boolean),
									),
								),
							)
						}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Palavras-chave"
								placeholder="Digite e tecle Enter"
								error={!!fieldError("keywords")}
								helperText={
									fieldError("keywords") ??
									`Procuradas no modelo do veículo. Até ${MAX_KEYWORDS}; não use a marca aqui.`
								}
							/>
						)}
					/>
				</Grid>

				<Grid item xs={12} md={6}>
					<TextField
						select
						fullWidth
						id="keyword_source"
						name="keyword_source"
						label="Origem da palavra-chave"
						required={values.keywords.length > 0}
						value={values.keyword_source}
						onChange={handleChange}
						error={!!fieldError("keyword_source")}
						helperText={
							fieldError("keyword_source") ?? "Onde procurar as palavras-chave."
						}
					>
						{options.keyword_sources.map((option) => (
							<MenuItem key={option.value} value={option.value}>
								{option.label}
							</MenuItem>
						))}
					</TextField>
				</Grid>

				<Grid item xs={12} md={6}>
					<Autocomplete
						multiple
						disableCloseOnSelect
						options={SEAT_OPTIONS}
						getOptionLabel={(seats) => String(seats)}
						value={values.seats}
						onChange={(_event, seats) => setFieldValue("seats", seats)}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Lugares"
								error={!!fieldError("seats")}
								helperText={
									fieldError("seats") ??
									`Até ${MAX_SEATS}. Em branco, vale para qualquer quantidade.`
								}
							/>
						)}
					/>
				</Grid>

				<Grid
					item
					xs={12}
					md={6}
					sx={{ display: "flex", alignItems: "center" }}
				>
					<FormControlLabel
						control={
							<Switch
								checked={values.active}
								onChange={(e) => setFieldValue("active", e.target.checked)}
							/>
						}
						label={doorRuleActiveLabel(values.active)}
					/>
				</Grid>
			</Grid>

			<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 4 }}>
				<Button variant="outlined" onClick={() => navigate(LIST_PATH)}>
					Cancelar
				</Button>
				<Button type="submit" variant="contained" disabled={isSubmitting}>
					Salvar
				</Button>
			</Box>
		</Box>
	);
}

export default function CreateOrUpdateDoorRule(): JSX.Element {
	const { id } = useParams();
	const navigate = useNavigate();
	const { user } = useAuth();
	const { setTitleBar, setPathTitleBar } = useCore();
	const isAdmin = user?.profile === "3";
	const [options, setOptions] = useState<DoorRuleOptions | null>(null);
	const [initialValues, setInitialValues] = useState<Values | null>(null);

	useLayoutEffect(() => {
		setTitleBar(id ? "Editar regra de portas" : "Nova regra de portas");
		setPathTitleBar(undefined);
	}, [id, setTitleBar, setPathTitleBar]);

	useEffect(() => {
		if (!isAdmin) return;
		let ignore = false;
		Promise.all([
			getDoorRuleOptions(),
			id ? getDoorRule(id) : Promise.resolve(null),
		])
			.then(([loadedOptions, rule]) => {
				if (ignore) return;
				setOptions(loadedOptions);
				setInitialValues(rule ? ruleToValues(rule) : emptyValues);
			})
			.catch((error) => {
				if (ignore) return;
				const message =
					error instanceof Error
						? error.message
						: "Não foi possível carregar a regra.";
				toast.error(message, {
					position: toast.POSITION.TOP_RIGHT,
					toastId: LOAD_ERROR_TOAST_ID,
				});
				navigate(LIST_PATH);
			});
		return () => {
			ignore = true;
		};
	}, [id, isAdmin, navigate]);

	if (!isAdmin) return <NotAuthorized />;
	if (!options || !initialValues) return <LoadingPage />;

	return (
		<DoorRuleForm id={id} options={options} initialValues={initialValues} />
	);
}
